import { IMAGES } from "@/data/content";

// One dedicated, SEO-optimized landing page per service.
export const SERVICE_PAGES = [
  {
    slug: "malware-detection",
    tag: "01",
    accent: "#22D3EE",
    image: IMAGES.padlock,
    nav: "Malware Detection",
    h1: "Malware Detection & Prevention",
    metaTitle: "Malware Detection & Prevention | Endasphere Edge Security",
    metaDescription:
      "Locate, quarantine and neutralize malware before it reaches your origin. Endasphere runs zero-day behavioral heuristics at 312 edge locations to stop threats in real time.",
    keywords: "malware detection, malware prevention, zero-day protection, edge security, WAF, threat quarantine",
    intro:
      "Endasphere inspects every request at the edge, detecting and neutralizing malware, injections and malicious payloads before they ever touch your origin servers.",
    features: [
      { title: "Zero-day heuristics", desc: "Behavioral engines flag never-before-seen threats without waiting for signatures." },
      { title: "Inline quarantine", desc: "Malicious payloads are isolated and dropped at the edge, not at your server." },
      { title: "Continuous scanning", desc: "Origins and assets are scanned around the clock for injected or compromised code." },
      { title: "Global intelligence", desc: "Threats seen anywhere in the sphere instantly harden defenses everywhere." },
    ],
    faqs: [
      { q: "How does edge malware detection differ from an antivirus?", a: "Antivirus runs on endpoints after delivery. Endasphere inspects traffic in transit at the edge, stopping malware before it reaches your infrastructure or your users." },
      { q: "Does it catch zero-day threats?", a: "Yes. Behavioral heuristics detect anomalous, never-before-seen payloads rather than relying solely on known signatures." },
    ],
  },
  {
    slug: "privacy-shield",
    tag: "02",
    accent: "#FACC15",
    image: IMAGES.mesh,
    nav: "Privacy Shield",
    h1: "Privacy Shield",
    metaTitle: "Privacy Shield — Encrypted, Tracker-Free Connectivity | Endasphere",
    metaDescription:
      "Encrypt every hop, strip trackers and keep your users invisible. Endasphere's Privacy Shield hardens data in transit across a global reverse-proxy network.",
    keywords: "online privacy, encryption, tracker blocking, data protection, private connectivity, secure proxy",
    intro:
      "Privacy is the default inside the sphere. Endasphere encrypts every connection, strips invasive trackers and keeps your users' data private end to end.",
    features: [
      { title: "End-to-end encryption", desc: "Every hop between user and origin is encrypted and authenticated." },
      { title: "Tracker stripping", desc: "Invasive third-party trackers are removed before they load." },
      { title: "Data minimization", desc: "We collect only what's needed and anonymize by default." },
      { title: "Compliance-ready", desc: "Controls that support GDPR and modern privacy regulations." },
    ],
    faqs: [
      { q: "Does Privacy Shield slow down my site?", a: "No — encryption and tracker stripping happen inline at the edge, often improving load times by removing heavy third-party scripts." },
      { q: "Can I keep analytics while protecting privacy?", a: "Yes. You choose which trusted analytics to allow while blocking invasive trackers." },
    ],
  },
  {
    slug: "theft-prevention",
    tag: "03",
    accent: "#F43F5E",
    image: IMAGES.padlock,
    nav: "Theft Prevention",
    h1: "Account Theft Prevention",
    metaTitle: "Account Theft & Fraud Prevention | Endasphere Identity Security",
    metaDescription:
      "Stop account takeover cold. Endasphere blocks credential stuffing and detects identity anomalies in real time to prevent theft and fraud across your applications.",
    keywords: "account takeover prevention, credential stuffing protection, fraud prevention, identity security, bot mitigation",
    intro:
      "Endasphere builds a wall around identity — blocking credential-stuffing bots and flagging anomalous behavior before an account is ever compromised.",
    features: [
      { title: "Credential-stuffing walls", desc: "Automated login attacks are throttled and blocked at the edge." },
      { title: "Anomaly detection", desc: "Unusual location, device or behavior triggers step-up challenges." },
      { title: "Bot mitigation", desc: "Distinguish real users from automated fraud in milliseconds." },
      { title: "Real-time alerts", desc: "Security teams are notified the moment takeover patterns emerge." },
    ],
    faqs: [
      { q: "What is credential stuffing?", a: "Attackers replay stolen username/password pairs across sites. Endasphere detects and blocks these high-velocity automated attempts before they succeed." },
      { q: "Will legitimate users be blocked?", a: "No — risk scoring challenges only suspicious sessions, keeping friction near zero for genuine users." },
    ],
  },
  {
    slug: "game-cheat-detection",
    tag: "04",
    accent: "#22D3EE",
    image: IMAGES.gaming,
    nav: "Game Cheat Detection",
    h1: "Real-Time Game Cheat Detection",
    metaTitle: "Real-Time Game Cheat Detection & Anti-Cheat | Endasphere",
    metaDescription:
      "Catch aimbots, wallhacks and injectors the instant they load. Endasphere's real-time anti-cheat uses sub-frame telemetry to enforce fair play in online multiplayer games.",
    keywords: "game cheat detection, anti-cheat, aimbot detection, wallhack detection, anti-cheat software, real-time cheat prevention, esports integrity",
    intro:
      "Fair play, enforced in real time. Endasphere analyzes sub-frame telemetry to detect cheats the instant they activate — protecting competitive integrity for players and studios.",
    features: [
      { title: "Sub-frame telemetry", desc: "Detects aimbots and triggerbots by analyzing input at frame-level granularity." },
      { title: "Injector detection", desc: "Flags memory injectors and unauthorized processes as they load." },
      { title: "Wallhack signatures", desc: "Identifies rendering and data anomalies used to see through walls." },
      { title: "Live enforcement", desc: "Ban, kick or challenge suspected cheaters in real time." },
    ],
    faqs: [
      { q: "Does anti-cheat add latency for players?", a: "No. Detection runs at the edge and in parallel, so gameplay stays smooth while cheats are flagged instantly." },
      { q: "What cheats can Endasphere detect?", a: "Aimbots, triggerbots, wallhacks, memory injectors and automation scripts — with continuously updated signatures." },
    ],
  },
  {
    slug: "cloud-servers",
    tag: "05",
    accent: "#FACC15",
    image: IMAGES.serverRoom,
    nav: "Cloud Servers",
    h1: "Protected Cloud Servers",
    metaTitle: "Secure Cloud Servers — Elastic Compute in the Sphere | Endasphere",
    metaDescription:
      "Deploy elastic cloud compute wrapped in Endasphere's security perimeter. Scalable, protected servers with built-in DDoS mitigation and edge acceleration.",
    keywords: "secure cloud servers, cloud hosting, elastic compute, protected hosting, DDoS protected servers, cloud infrastructure",
    intro:
      "Compute that's protected by default. Endasphere cloud servers scale elastically and sit inside the same security perimeter that shields the rest of your stack.",
    features: [
      { title: "Elastic scaling", desc: "Scale compute up and down instantly to match demand." },
      { title: "Built-in DDoS shield", desc: "Every server is protected by the global mitigation network." },
      { title: "Deploy anywhere", desc: "Spin up workloads close to your users across 312 locations." },
      { title: "Unified control plane", desc: "Manage compute, security and delivery from one dashboard." },
    ],
    faqs: [
      { q: "Are cloud servers protected out of the box?", a: "Yes — DDoS mitigation, WAF and edge acceleration are included by default, no extra configuration required." },
      { q: "Can I deploy in specific regions?", a: "Yes, workloads can be placed close to your users across our global edge footprint." },
    ],
  },
  {
    slug: "cdn",
    tag: "06",
    accent: "#22D3EE",
    image: IMAGES.network,
    nav: "Reverse Proxy & CDN",
    h1: "Reverse Proxy & Global CDN",
    metaTitle: "Reverse Proxy CDN — Faster, Safer Websites & APIs | Endasphere",
    metaDescription:
      "Endasphere sits between users and your origin as a reverse proxy and CDN — caching content, filtering threats and cutting latency to 11ms across 312 edge locations.",
    keywords: "reverse proxy, CDN, content delivery network, edge caching, API acceleration, DDoS protection, website performance, low latency",
    intro:
      "Acting as a reverse proxy between users and your origin servers, Endasphere accelerates, caches and hardens every request across a global backbone.",
    features: [
      { title: "Edge caching", desc: "Serve content from the nearest of 312 nodes for instant load times." },
      { title: "Origin shielding", desc: "Your origin is hidden and protected behind the proxy layer." },
      { title: "Traffic filtering", desc: "Malicious and bot traffic is dropped before it reaches you." },
      { title: "11ms average latency", desc: "Intelligent routing keeps every connection blazing fast." },
    ],
    faqs: [
      { q: "How does a reverse proxy improve security?", a: "It hides your origin, absorbs attacks and filters malicious traffic at the edge, so threats never reach your servers." },
      { q: "Will a CDN speed up my API?", a: "Yes — edge caching and intelligent routing reduce round-trip time, lowering latency for both content and API responses." },
    ],
  },
];

export const getService = (slug) => SERVICE_PAGES.find((s) => s.slug === slug);
